import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { ToggleButton } from 'components/Input/ToggleButton'
import { Input } from 'components/Input/Input'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { NavBar } from 'components/NavBar/NavBar'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { ISelectValue } from 'constants/globalTypes'
import { getMachineryCategories } from 'features/categories/machineryCategories'
import { getLocationRegistryTree } from 'features/locationRegistry/locationRegistrySlice'
import {
  acceptProposal,
  activateDrafts,
  activateSegment,
  calculateRoute,
  cancelRoadEvent,
  clearSiteNetwork,
  clearSiteNetworkRoute,
  createGeofence,
  dismissProposal,
  drawRoadSegment,
  hydrateDraftFeatures,
  getGeofences,
  getMachineClasses,
  getMachineEta,
  getNetworkGraph,
  getProposals,
  getRoadEvents,
  retireSegment,
  setMachineClass,
  setMachineEta,
  setSiteNodeId,
} from 'features/siteNetwork/siteNetworkSlice'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { tokens } from 'styling/tokens'
import { Button, Flex, MainHeadline, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { ILocationNode } from 'types/locationsRegistry.interface'
import { GeofenceKind, OffroadClusterStatus, RoadClass, RoadEventType } from 'types/siteNetworkEnums.interface'
import { AnchorModal } from './AnchorModal'
import { CloseRoadModal } from './CloseRoadModal'
import { NetworkMap } from './NetworkMap'
import {
  defaultRoadClassColor,
  machineClassOptions,
  proposalStatusLabel,
  roadClassColors,
  roadClassOptions,
  roadDraftColor,
  roadEventTypeLabel,
  vehicleOptions,
} from './siteNetworkData'
import {
  EventRow,
  MappingRow,
  Hint,
  Layout,
  LegendDot,
  LegendRow,
  MapArea,
  MapToolsPanel,
  OpsPanel,
  OpsTab,
  OpsTabBar,
  OpsTabContent,
  StatChip,
  StatChips,
  StatsBar,
  Workspace,
} from './SiteNetwork.styled'

const fmtMin = (s: number): string =>
  `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')} min`

const countLineStrings = (features?: { geometry: { type: string } }[]): number =>
  features?.filter((f) => f.geometry.type === 'LineString').length ?? 0

const collectSites = (node: ILocationNode | null): ILocationNode[] => {
  if (!node) return []
  const sites: ILocationNode[] = []
  const walk = (n: ILocationNode) => {
    if (n.type === 'SITE' && n.id > 0) sites.push(n)
    ;(n.children || []).forEach(walk)
  }
  walk(node)
  return sites
}

const collectLocationsUnder = (node: ILocationNode | null, siteId: number): ILocationNode[] => {
  if (!node) return []
  let site: ILocationNode | null = null
  const find = (n: ILocationNode) => {
    if (n.id === siteId) site = n
    ;(n.children || []).forEach(find)
  }
  find(node)
  if (!site) return []
  const out: ILocationNode[] = []
  const walk = (n: ILocationNode) => {
    if (n.id !== siteId) out.push(n)
    ;(n.children || []).forEach(walk)
  }
  walk(site)
  return out
}

type MapMode = 'view' | 'draw' | 'routeOrigin' | 'routeDest' | 'geofence'
type OpsTabId = 'events' | 'proposals' | 'eta' | 'setup'
type SiteNetworkTabId = 'map' | 'config'
const draftStorageKey = (siteNodeId: number) => `siteNetworkDrafts:${siteNodeId}`

export const SiteNetwork = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    siteNodeId,
    graph,
    draftFeatures,
    geofences,
    roadEvents,
    proposals,
    machineClasses,
    route,
    isRouting,
  } = useSelector((state: RootState) => state.siteNetwork)
  const { tree } = useSelector((state: RootState) => state.locationRegistry)
  const { machineryCategories } = useSelector((state: RootState) => state.machineryCategories)

  const [siteId, setSiteId] = useState<number | null>(null)
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [originCoord, setOriginCoord] = useState<number[] | null>(null)
  const [destCoord, setDestCoord] = useState<number[] | null>(null)
  const [vehicle, setVehicle] = useState(String(vehicleOptions[0].value))

  const [mode, setMode] = useState<MapMode>('view')
  const [cls, setCls] = useState(RoadClass.HaulPrimary)
  const [name, setName] = useState('')
  const [oneWay, setOneWay] = useState(false)
  const [pts, setPts] = useState<[number, number][]>([])
  const [geofencePts, setGeofencePts] = useState<[number, number][]>([])
  const [geofenceName, setGeofenceName] = useState('')
  const [geofenceKind, setGeofenceKind] = useState(GeofenceKind.RestrictedArea)
  const [geofenceSpeed, setGeofenceSpeed] = useState('')

  const [closeModal, setCloseModal] = useState<{
    segmentId: string
    segmentName: string | null
  } | null>(null)
  const [anchorOpen, setAnchorOpen] = useState(false)
  const [selectedSegment, setSelectedSegment] = useState<{
    id: string
    name: string | null
  } | null>(null)
  const [etaMachineId, setEtaMachineId] = useState('')
  const [etaInfo, setEtaInfo] = useState<string | null>(null)
  const [opsTab, setOpsTab] = useState<OpsTabId>('events')
  const [siteNetworkTab, setSiteNetworkTab] = useState<SiteNetworkTabId>('map')

  useEffect(() => {
    dispatch(getLocationRegistryTree())
    dispatch(getMachineClasses())
    dispatch(getMachineryCategories({ page: 1, limit: 100 }))
    return () => {
      dispatch(clearSiteNetwork())
    }
  }, [dispatch])

  useEffect(() => {
    if (!siteId) return
    dispatch(setSiteNodeId(siteId))
    dispatch(getNetworkGraph(siteId))
    dispatch(getGeofences({ siteNodeId: siteId }))
    dispatch(getRoadEvents(siteId))
    dispatch(getProposals(siteId))
    setPts([])
    setGeofencePts([])
    setOriginCoord(null)
    setDestCoord(null)
    setSelectedSegment(null)
    dispatch(clearSiteNetworkRoute())
    try {
      const raw = localStorage.getItem(draftStorageKey(siteId))
      const parsed = raw ? (JSON.parse(raw) as any[]) : []
      const normalized = (parsed || []).filter(
        (d) =>
          d?.id &&
          d?.fromNodeId &&
          d?.toNodeId &&
          Array.isArray(d?.coordinates) &&
          d.coordinates.length >= 2,
      )
      dispatch(hydrateDraftFeatures(normalized))
    } catch {
      dispatch(hydrateDraftFeatures([]))
    }
  }, [siteId, dispatch])

  useEffect(() => {
    if (!siteId) return
    localStorage.setItem(draftStorageKey(siteId), JSON.stringify(draftFeatures))
  }, [siteId, draftFeatures])

  const siteOptions = useMemo(
    () =>
      collectSites(tree).map((s) => ({
        label: s.name,
        value: s.id,
      })),
    [tree],
  )

  const locationOptions = useMemo(
    () =>
      collectLocationsUnder(tree, siteId || 0).map((l) => ({
        label: `${l.name} (${l.type})`,
        value: l.id,
      })),
    [tree, siteId],
  )

  const nodeOptions = useMemo(() => {
    const opts: { label: string; value: string }[] = []
    ;(graph?.features || []).forEach((f) => {
      if (f.geometry?.type !== 'Point') return
      const id = String(f.properties?.id ?? '')
      if (!id) return
      const kind = f.properties?.kind
      opts.push({ label: `${id.slice(0, 8)}… (${kind ?? 'node'})`, value: id })
    })
    draftFeatures.forEach((d) => {
      opts.push({ label: `Draft from ${d.fromNodeId.slice(0, 8)}…`, value: d.fromNodeId })
      opts.push({ label: `Draft to ${d.toNodeId.slice(0, 8)}…`, value: d.toNodeId })
    })
    return opts
  }, [graph, draftFeatures])

  const categoryOptions = useMemo(() => {
    const list = (machineryCategories || []) as { id: number; name: string }[]
    return list.map((c) => ({ label: c.name, value: c.id }))
  }, [machineryCategories])

  const closedIds = useMemo(
    () =>
      roadEvents
        .filter((e) => Number(e.type) === RoadEventType.Closed && e.segmentId)
        .map((e) => e.segmentId as string),
    [roadEvents],
  )

  const segmentCount = countLineStrings(graph?.features)
  const draftCount = draftFeatures.length
  const openProposals = proposals.filter((p) => Number(p.status) === OffroadClusterStatus.Proposed)

  const runRoute = () => {
    if (!siteId) return
    const [machineClassRaw, loadStateRaw] = vehicle.split('|')
    const machineClass = Number(machineClassRaw)
    const loadState = Number(loadStateRaw)
    const originEp = originCoord
      ? { coord: originCoord }
      : origin
        ? { locationId: Number(origin) }
        : null
    const destEp = destCoord
      ? { coord: destCoord }
      : destination
        ? { locationId: Number(destination) }
        : null
    if (!originEp || !destEp) return
    dispatch(
      calculateRoute({
        siteNodeId: siteId,
        body: { origin: originEp, destination: destEp, machineClass, loadState },
      }),
    )
  }

  const onSegmentClick = (segmentId: string, segmentName: string | null) => {
    if (mode !== 'view') return
    setSelectedSegment({ id: segmentId, name: segmentName })
  }

  const saveDrawRoad = () => {
    if (!siteId) return
    if (pts.length >= 2) {
      dispatch(
        drawRoadSegment({
          siteNodeId: siteId,
          coordinates: pts,
          class: Number(cls),
          name: name || undefined,
          oneWay,
        }),
      )
    }
    setPts([])
    setName('')
  }

  const saveGeofence = () => {
    if (!siteId || !geofenceName.trim()) return
    const ring: number[][] = geofencePts
    if (ring.length < 3) {
      return
    }
    const first = ring[0]
    const closed =
      first[0] === ring[ring.length - 1][0] && first[1] === ring[ring.length - 1][1]
        ? ring
        : [...ring, [...first]]
    if (closed.length < 4) {
      setGeofencePts([])
      return
    }
    dispatch(
      createGeofence({
        siteNodeId: siteId,
        body: {
          name: geofenceName.trim(),
          kind: geofenceKind,
          coordinates: closed,
          meta:
            geofenceKind === GeofenceKind.SpeedZone && geofenceSpeed
              ? { ['speed_kmh']: Number(geofenceSpeed) }
              : undefined,
        },
      }),
    )
    setGeofencePts([])
    setGeofenceName('')
    setMode('view')
  }

  const handleMapClick = (c: [number, number]) => {
    if (mode === 'draw') setPts((prev) => [...prev, c])
    if (mode === 'geofence') setGeofencePts((prev) => [...prev, c])
    if (mode === 'routeOrigin') {
      setOriginCoord(c)
      setOrigin('')
      setMode('routeDest')
    }
    if (mode === 'routeDest') {
      setDestCoord(c)
      setDestination('')
      setMode('view')
    }
  }

  const handleMapDoubleClick = () => {
    return
  }

  const mapMode = mode === 'draw' || mode === 'geofence' ? 'draw' : 'view'
  const drawCoordinates =
    mode === 'draw'
      ? pts
      : mode === 'geofence'
        ? geofencePts.length > 2
          ? [...geofencePts, geofencePts[0]]
          : geofencePts
        : ([] as [number, number][])

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between center mb='16' wrap gap='1rem'>
          <MainHeadline>Site Network</MainHeadline>
          <HonestSelectInput
            nomarg
            labelText='Site'
            options={siteOptions}
            defaultValue={siteId ?? ''}
            onChange={(v: ISelectValue) => setSiteId(Number(v.value))}
          />
        </Flex>
        {!siteId && (
          <Hint>Select a site from the location registry to load and edit its road network.</Hint>
        )}
        {siteId && (
          <Layout>
            <StatsBar>
              <StatChips>
                <StatChip>
                  Segments<b>{segmentCount}</b>
                </StatChip>
                <StatChip>
                  Drafts<b>{draftCount}</b>
                </StatChip>
                <StatChip>
                  Events<b>{roadEvents.length}</b>
                </StatChip>
                <StatChip>
                  Proposals<b>{openProposals.length}</b>
                </StatChip>
              </StatChips>
              <LegendRow>
                <LegendDot $color={roadClassColors[RoadClass.HaulPrimary]} /> Haul
                <LegendDot $color={defaultRoadClassColor} /> Service
                <LegendDot $color={tokens.status.danger.fg} /> Closed
                <LegendDot $color={roadDraftColor} /> Draft
              </LegendRow>
            </StatsBar>

            <TabsContainer>
              <Tabs active={siteNetworkTab === 'map'} onClick={() => setSiteNetworkTab('map')}>
                Map
              </Tabs>
              <Tabs active={siteNetworkTab === 'config'} onClick={() => setSiteNetworkTab('config')}>
                Config
              </Tabs>
            </TabsContainer>

            {siteNetworkTab === 'config' && (
              <OpsPanel>
                <OpsTabBar>
                  <OpsTab $active={opsTab === 'events'} onClick={() => setOpsTab('events')}>
                    Road events ({roadEvents.length})
                  </OpsTab>
                  <OpsTab $active={opsTab === 'proposals'} onClick={() => setOpsTab('proposals')}>
                    Proposals ({openProposals.length})
                  </OpsTab>
                  <OpsTab $active={opsTab === 'eta'} onClick={() => setOpsTab('eta')}>
                    Machine ETA
                  </OpsTab>
                  <OpsTab $active={opsTab === 'setup'} onClick={() => setOpsTab('setup')}>
                    Network setup
                  </OpsTab>
                </OpsTabBar>
                <OpsTabContent>
                  {opsTab === 'events' && (
                    <>
                      {roadEvents.length === 0 && (
                        <Hint>No active events. Select a road in View mode to create one.</Hint>
                      )}
                      {roadEvents.map((e) => (
                        <EventRow key={e.id}>
                          <Flex center gap='0.5rem'>
                            <StatusPill
                              status={
                                Number(e.type) === RoadEventType.Closed ? 'danger' : 'warning'
                              }
                            >
                              {roadEventTypeLabel(e.type)}
                            </StatusPill>
                            <BodyText>{e.reason}</BodyText>
                          </Flex>
                          <Button
                            small
                            onClick={() =>
                              dispatch(cancelRoadEvent({ siteNodeId: siteId, id: e.id }))
                            }
                          >
                            Cancel
                          </Button>
                        </EventRow>
                      ))}
                    </>
                  )}
                  {opsTab === 'proposals' && (
                    <>
                      {proposals.length === 0 && (
                        <Hint>No proposals yet. Generated when machines repeatedly drive off-network.</Hint>
                      )}
                      {proposals.map((p) => (
                        <EventRow key={p.id}>
                          <Flex column gap='0.25rem'>
                            <BodyText>
                              {p.pingCount} pings · {p.machineCount} machines
                            </BodyText>
                            <Hint>
                              {proposalStatusLabel(Number(p.status))} · last{' '}
                              {new Date(p.lastSeen).toLocaleString()}
                            </Hint>
                          </Flex>
                          {Number(p.status) === OffroadClusterStatus.Proposed && (
                            <Flex gap='0.5rem'>
                              <Button
                                small
                                orange
                                onClick={() =>
                                  dispatch(acceptProposal({ siteNodeId: siteId, id: p.id }))
                                }
                              >
                                Accept
                              </Button>
                              <Button
                                small
                                onClick={() =>
                                  dispatch(dismissProposal({ siteNodeId: siteId, id: p.id }))
                                }
                              >
                                Dismiss
                              </Button>
                            </Flex>
                          )}
                        </EventRow>
                      ))}
                    </>
                  )}
                  {opsTab === 'eta' && (
                    <>
                      <Input
                        nomarg
                        type='number'
                        labelText='Machine id'
                        placeholder='Fleet register id'
                        value={etaMachineId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEtaMachineId(e.target.value)
                        }
                      />
                      <Flex gap='0.5rem' wrap>
                        <Button
                          small
                          disabled={!etaMachineId || (!destination && !destCoord)}
                          orange
                          onClick={async () => {
                            if (!siteId || !etaMachineId) return
                            const destinationEp = destCoord
                              ? { coord: destCoord }
                              : destination
                                ? { locationId: Number(destination) }
                                : null
                            if (!destinationEp) return
                            const resp = await dispatch(
                              setMachineEta({
                                siteNodeId: siteId,
                                machineId: Number(etaMachineId),
                                body: { destination: destinationEp },
                              }),
                            )
                            if (resp.meta.requestStatus === 'fulfilled') {
                              const data = resp.payload as {
                                active: boolean
                                etaSeconds?: number
                              }
                              setEtaInfo(
                                data.active && data.etaSeconds != null
                                  ? `ETA ${fmtMin(data.etaSeconds)}`
                                  : 'Destination set',
                              )
                            }
                          }}
                        >
                          Set destination
                        </Button>
                        <Button
                          small
                          disabled={!etaMachineId}
                          onClick={async () => {
                            if (!siteId || !etaMachineId) return
                            const resp = await dispatch(
                              getMachineEta({
                                siteNodeId: siteId,
                                machineId: Number(etaMachineId),
                              }),
                            )
                            if (resp.meta.requestStatus === 'fulfilled') {
                              const data = resp.payload as {
                                active: boolean
                                etaSeconds?: number
                              }
                              setEtaInfo(
                                !data.active
                                  ? 'No active trip'
                                  : data.etaSeconds != null
                                    ? `ETA ${fmtMin(data.etaSeconds)}`
                                    : 'Active (no ETA yet)',
                              )
                            }
                          }}
                        >
                          Get ETA
                        </Button>
                      </Flex>
                      {etaInfo && <BodyText>{etaInfo}</BodyText>}
                      <Hint>Uses destination from Route tester (location or map pick).</Hint>
                    </>
                  )}
                  {opsTab === 'setup' && (
                    <>
                      <Button width100 onClick={() => setAnchorOpen(true)}>
                        Anchor location to node
                      </Button>
                      <Hint style={{ marginTop: '0.5rem' }}>
                        Unmapped machine categories are treated as haul truck for learning.
                      </Hint>
                      {machineClasses.map((m) => (
                        <MappingRow key={m.machineCategoryId}>
                          <BodyText>{m.machineCategoryName}</BodyText>
                          <HonestSelectInput
                            nomarg
                            options={machineClassOptions}
                            defaultValue={m.machineClass}
                            onChange={(v: ISelectValue) =>
                              dispatch(
                                setMachineClass({
                                  machineCategoryId: m.machineCategoryId,
                                  body: { machineClass: Number(v.value) },
                                }),
                              )
                            }
                          />
                        </MappingRow>
                      ))}
                      {categoryOptions.length > 0 && (
                        <HonestSelectInput
                          nomarg
                          labelText='Add mapping for category'
                          options={categoryOptions.filter(
                            (c) => !machineClasses.some((m) => m.machineCategoryId === c.value),
                          )}
                          defaultValue=''
                          onChange={(v: ISelectValue) =>
                            dispatch(
                              setMachineClass({
                                machineCategoryId: Number(v.value),
                                body: { machineClass: machineClassOptions[0].value as number },
                              }),
                            )
                          }
                        />
                      )}
                      {machineClasses.length === 0 && (
                        <Hint>No mappings yet. Select a category above to create one.</Hint>
                      )}
                    </>
                  )}
                </OpsTabContent>
              </OpsPanel>
            )}

            {siteNetworkTab === 'map' && (
              <Workspace>
              <MapArea>
                <NetworkMap
                  key={siteId}
                  graph={graph}
                  draftFeatures={draftFeatures}
                  closedSegmentIds={closedIds}
                  routeGeometry={route?.geometry ?? null}
                  drawCoordinates={drawCoordinates}
                  geofences={geofences}
                  pickCoordinates={{ origin: originCoord, destination: destCoord }}
                  mode={mapMode}
                  onSegmentClick={onSegmentClick}
                  onMapClick={handleMapClick}
                  onMapDoubleClick={handleMapDoubleClick}
                />
              </MapArea>

              <MapToolsPanel>
                <Container>
                  <ContainerHeader>
                    <TitleText weight='600'>Map editor</TitleText>
                  </ContainerHeader>
                  <ContainerContent gap='0.5rem'>
                    <TabsContainer>
                      <Tabs
                        active={mode === 'view'}
                        onClick={() => {
                          setMode('view')
                          setPts([])
                          setGeofencePts([])
                        }}
                      >
                        View
                      </Tabs>
                      <Tabs
                        active={mode === 'draw'}
                        onClick={() => {
                          setMode('draw')
                          setGeofencePts([])
                        }}
                      >
                        Draw road
                      </Tabs>
                      <Tabs
                        active={mode === 'geofence'}
                        onClick={() => {
                          setMode('geofence')
                          setPts([])
                        }}
                      >
                        Geofence
                      </Tabs>
                    </TabsContainer>
                    {mode === 'draw' && (
                      <>
                        <HonestSelectInput
                          nomarg
                          labelText='Road class'
                          defaultValue={cls}
                          options={roadClassOptions}
                          onChange={(value: ISelectValue) => setCls(Number(value.value))}
                        />
                        <Input
                          nomarg
                          labelText='Name (optional)'
                          placeholder='e.g. Ramp 3'
                          value={name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                        />
                        <ToggleButton
                          start
                          label='One-way (in draw direction)'
                          state={oneWay}
                          handleChange={() => setOneWay((prev) => !prev)}
                        />
                        <Hint>
                          Click vertices on the map (≥2), then Save road. Endpoints snap within 30 m.
                        </Hint>
                        <Flex gap='0.5rem' wrap>
                          <Button small orange disabled={pts.length < 2} onClick={saveDrawRoad}>
                            Save road
                          </Button>
                          <Button small onClick={() => setPts([])}>
                            Cancel
                          </Button>
                        </Flex>
                      </>
                    )}
                    {mode === 'geofence' && (
                      <>
                        <Input
                          nomarg
                          labelText='Name'
                          value={geofenceName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setGeofenceName(e.target.value)
                          }
                        />
                        <HonestSelectInput
                          nomarg
                          labelText='Kind'
                          defaultValue={geofenceKind}
                          options={[
                            { label: 'Location footprint', value: GeofenceKind.LocationFootprint },
                            { label: 'Blast exclusion', value: GeofenceKind.BlastExclusion },
                            { label: 'Restricted area', value: GeofenceKind.RestrictedArea },
                            { label: 'Speed zone', value: GeofenceKind.SpeedZone },
                            { label: 'Gate', value: GeofenceKind.Gate },
                            { label: 'Site boundary', value: GeofenceKind.SiteBoundary },
                          ]}
                          onChange={(v: ISelectValue) => setGeofenceKind(Number(v.value))}
                        />
                        {geofenceKind === GeofenceKind.SpeedZone && (
                          <Input
                            nomarg
                            type='number'
                            labelText='Speed (km/h)'
                            value={geofenceSpeed}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setGeofenceSpeed(e.target.value)
                            }
                          />
                        )}
                        <Hint>Click ≥3 vertices on the map, then Save geofence.</Hint>
                        <Flex gap='0.5rem' wrap>
                          <Button
                            small
                            orange
                            disabled={!geofenceName.trim() || geofencePts.length < 3}
                            onClick={saveGeofence}
                          >
                            Save geofence
                          </Button>
                          <Button small onClick={() => setGeofencePts([])}>
                            Cancel
                          </Button>
                        </Flex>
                      </>
                    )}
                    {selectedSegment && mode === 'view' && (
                      <>
                        <BodyText>{selectedSegment.name ?? selectedSegment.id}</BodyText>
                        <Flex gap='0.5rem' wrap>
                          <Button
                            small
                            orange
                            onClick={() =>
                              setCloseModal({
                                segmentId: selectedSegment.id,
                                segmentName: selectedSegment.name,
                              })
                            }
                          >
                            Road event
                          </Button>
                          <Button
                            small
                            onClick={() =>
                              dispatch(
                                activateSegment({ siteNodeId: siteId, id: selectedSegment.id }),
                              )
                            }
                          >
                            Activate
                          </Button>
                          <Button
                            small
                            onClick={() =>
                              dispatch(retireSegment({ siteNodeId: siteId, id: selectedSegment.id }))
                            }
                          >
                            Retire
                          </Button>
                        </Flex>
                      </>
                    )}
                    <Button
                      orange
                      width100
                      disabled={draftCount === 0}
                      onClick={() => dispatch(activateDrafts(siteId))}
                    >
                      Activate drafts ({draftCount})
                    </Button>
                  </ContainerContent>
                </Container>

                <Container>
                  <ContainerHeader>
                    <TitleText weight='600'>Route tester</TitleText>
                  </ContainerHeader>
                  <ContainerContent gap='0.5rem'>
                    <HonestSelectInput
                      nomarg
                      labelText='Origin (location)'
                      defaultValue={origin}
                      options={locationOptions}
                      onChange={(value: ISelectValue) => {
                        setOrigin(String(value.value))
                        setOriginCoord(null)
                      }}
                    />
                    <HonestSelectInput
                      nomarg
                      labelText='Destination (location)'
                      defaultValue={destination}
                      options={locationOptions}
                      onChange={(value: ISelectValue) => {
                        setDestination(String(value.value))
                        setDestCoord(null)
                      }}
                    />
                    <Flex gap='0.5rem' wrap>
                      <Button
                        small
                        orange={mode === 'routeOrigin'}
                        onClick={() => setMode('routeOrigin')}
                      >
                        Pick origin
                      </Button>
                      <Button
                        small
                        orange={mode === 'routeDest'}
                        onClick={() => setMode('routeDest')}
                      >
                        Pick destination
                      </Button>
                    </Flex>
                    {(originCoord || destCoord) && (
                      <Hint>
                        {originCoord
                          ? `Origin: ${originCoord[0].toFixed(5)}, ${originCoord[1].toFixed(5)}`
                          : 'Origin: location'}
                        {' · '}
                        {destCoord
                          ? `Dest: ${destCoord[0].toFixed(5)}, ${destCoord[1].toFixed(5)}`
                          : 'Dest: location'}
                      </Hint>
                    )}
                    <HonestSelectInput
                      nomarg
                      labelText='Vehicle'
                      defaultValue={vehicle}
                      options={vehicleOptions}
                      onChange={(value: ISelectValue) => setVehicle(String(value.value))}
                    />
                    <Button
                      orange
                      width100
                      disabled={
                        isRouting ||
                        (!origin && !originCoord) ||
                        (!destination && !destCoord)
                      }
                      onClick={runRoute}
                    >
                      {isRouting ? 'Routing…' : 'Get route'}
                    </Button>
                    {route && (
                      <>
                        <MetricCard
                          label='Expected (p50)'
                          value={fmtMin(route.durationSeconds.p50)}
                          accentColor={tokens.accent.base}
                        />
                        <MetricCard
                          label='Plan-safe (p85)'
                          value={fmtMin(route.durationSeconds.p85)}
                        />
                        <MetricCard
                          label='Distance'
                          value={`${(route.distanceM / 1000).toFixed(2)} km`}
                        />
                        <MetricCard label='Confidence' value={route.confidence.level} />
                      </>
                    )}
                  </ContainerContent>
                </Container>
              </MapToolsPanel>
              </Workspace>
            )}
          </Layout>
        )}
      </PageWrapper>
      {closeModal && siteId && (
        <CloseRoadModal
          siteNodeId={siteId}
          segmentId={closeModal.segmentId}
          segmentName={closeModal.segmentName}
          closeModal={() => setCloseModal(null)}
        />
      )}
      {anchorOpen && siteId && (
        <AnchorModal
          siteNodeId={siteId}
          nodeOptions={nodeOptions}
          closeModal={() => setAnchorOpen(false)}
        />
      )}
    </>
  )
}
